import { ChangeDetectionStrategy, Component, EventEmitter, forwardRef, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BehaviorSubject, combineLatest, map, Observable, of } from 'rxjs';
import { catchError, switchMap, take } from 'rxjs/operators';

import { ApiParams, CoreApiService, queryAll } from '../../../core-api.service';
import { NotificationService } from '../../../notification.service';
import { LOCAL_STORAGE_ID } from './operation-storage-select.constants';
import { ProjectsService } from '../../../projects/projects.service';
import { PermissionAction } from '../../../shared/enums/permission-action.enum';
import { XferStorage } from '../../../shared/models/xfer.model';
import { TranslatePipe } from '../../../translation.service';

@Component({
  host: { 'test-id': 'operation-storage-select' },
  selector: 'operation-storage-select',
  styleUrls: ['./operation-storage-select.component.scss'],
  templateUrl: './operation-storage-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    TranslatePipe,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => OperationStorageSelect),
      multi: true,
    },
  ],
})
export class OperationStorageSelect implements OnInit, OnChanges, ControlValueAccessor {
  @Input() webUploaderV2Enabled: boolean;
  @Input() protectedId: string;
  @Input() title: string;
  @Input() placeholder: string;
  @Input() initialValue: string;
  @Input() optionsWidth: string;
  @Input() description: string;
  @Input() maxOptionsHeight = '350px';
  @Input() isSelectDisabled = false;
  @Input() queryParams: string[] = [];
  @Input() permissionAction: PermissionAction;

  @Output() readonly storageSelect = new EventEmitter<XferStorage>();

  private filterString$ = new BehaviorSubject<string>('');
  private onChange: (value: string) => void;
  private onTouched: () => void;

  private readonly localImport = 'operationStorageSelect.myComputer';

  protected readonly extraOptions: XferStorage[] = [{
    label: this.localImport,
    _id: LOCAL_STORAGE_ID,
    url: this.localImport,
  } as XferStorage];

  protected storages$: Observable<XferStorage[]>;
  protected currentValue = '';
  protected isDisabled = false;
  protected visibleOptionsSet = new Set();
  protected visibleExtraOptionsSet = new Set(this.extraOptions);

  constructor(
    private coreApi: CoreApiService,
    private translatePipe: TranslatePipe,
    private projectsService: ProjectsService,
    private notifications: NotificationService,
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.initialValue?.currentValue) {
      this.currentValue = this.initialValue;
    }

    if (changes.isSelectDisabled?.currentValue !== undefined) {
      this.isDisabled = this.isSelectDisabled;
    }
  }

  ngOnInit(): void {
    const queryParams: ApiParams = {
      sort: 'label',
      filter: [...this.queryParams, `refs.project=${this.protectedId}`, 'status_check=success'],
    };

    this.storages$ = this.projectsService.projectPermissions$.pipe(
      take(1),
      map(projectPermissions => {
        return projectPermissions[this.protectedId]?.includes(this.permissionAction);
      }),
      switchMap(hasPermission => {
        if (hasPermission) {
          return combineLatest([
            queryAll(this.coreApi.xfer.storages.query, queryParams),
            this.filterString$,
          ]).pipe(
            map(([storages, filterString]) => {
              const preparedFilter = filterString.toLowerCase().trim();
              this.visibleOptionsSet.clear();
              this.visibleExtraOptionsSet.clear();
              storages.forEach(storage => {
                if (storage.label.toLowerCase().includes(preparedFilter)) {
                  this.visibleOptionsSet.add(storage);
                }
              });

              this.extraOptions.forEach(storage => {
                if (this.translatePipe.transform(storage.label).toLowerCase().includes(preparedFilter)) {
                  this.visibleExtraOptionsSet.add(storage);
                }
              });

              return storages;
            }),
            catchError((e) => {
              if (e.status.toString().startsWith('5')) {
                this.notifications.error('operationsStorageSelect.notifications.tryLater');
              } else {
                this.notifications.error('operationsStorageSelect.notifications.failedToGetStorages');
              }
              return of([]);
            })
          );
        } else {
          return of([]);
        }
      }),
      catchError(() => {
        this.notifications.error('operationsStorageSelect.notifications.failedToGetPermissions');
        return of([]);
      })
    );
  }

  writeValue(value: string): void {
    this.currentValue = value;
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  protected filterChange(filter: string): void {
    this.filterString$.next(filter);
  }

  protected onStorageSelect(value: string, storages: XferStorage[]): void {
    if (value === this.currentValue) {
      return;
    }

    this.currentValue = value;
    const selectedExtraStorage = this.extraOptions.find(storage => storage._id === value);

    if (selectedExtraStorage) {
      this.storageSelect.emit({ ...selectedExtraStorage, url: this.translatePipe.transform(selectedExtraStorage.url), label: this.translatePipe.transform(selectedExtraStorage.url) });
    }
    else {
      const selectedStorage = storages.find(storage => storage._id === value);
      this.storageSelect.emit(selectedStorage);
    }

    if (this.onChange) {
      this.onChange(value);
    }
    if (this.onTouched) {
      this.onTouched();
    }
  }
}
